import React, { useEffect, useState } from 'react'
import { observer } from 'mobx-react-lite'
import moment from 'moment-timezone'
import { MDBContainer, MDBRow, MDBCol, MDBInput, MDBBtn } from 'mdb-react-ui-kit'
import { toast } from 'react-toastify'
import UserProfileService from '../../../../shared/services/UserProfile.service'
import UserPerformanceEntryService from '../../../../shared/services/UserPerformanceEntry.service'
import LedgerFactory from '../../../../shared/factories/ledger.factory'
import LedgerTransactionFactory from '../../../../shared/factories/ledger-transaction.factory'

import './WeeklyMarketingCommitmentForm.scss'

const getThisMonday = () => moment().startOf('isoWeek').format('YYYY-MM-DD')

const WeeklyMarketingCommitmentForm = () => {
  const [invested, setInvested] = useState(0)
  const [leadSpend, setLeadSpend] = useState(0)
  const [entryId, setEntryId] = useState(null)
  const [weekStart, setWeekStart] = useState(null)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    const load = async () => {
      const thisMonday = getThisMonday()
      const thisSunday = moment().endOf('isoWeek').format('YYYY-MM-DD')
      setWeekStart(thisMonday)
      const userId = UserProfileService.getUserId()
      if (!userId) return

      try {
        const [entryRes, ledgers] = await Promise.all([
          UserPerformanceEntryService.search({
            search: { user_id: userId, entry_date: thisMonday },
          }),
          LedgerFactory.search({
            search: { user_id: userId },
            pagination: false,
          }),
        ])
        const entry = (entryRes?.models || [])[0]
        if (entry) {
          setInvested(Number(entry.invested) || 0)
          setEntryId(entry.id)
        } else {
          setInvested(0)
          setEntryId(null)
        }

        const ledgerList = Array.isArray(ledgers) ? ledgers : ledgers?.models || []
        const ledger = ledgerList[0]
        if (!ledger) {
          setLeadSpend(0)
          return
        }
        const ledgerId = ledger.get ? ledger.get('id') : ledger.id?.() ?? ledger?.id
        const txRes = await LedgerTransactionFactory.search({
          search: { ledger_id: ledgerId, type: 'LeadVendorTransfer' },
          pagination: false,
        })
        const txList = Array.isArray(txRes) ? txRes : txRes?.models || []
        const weekStartM = moment(thisMonday)
        const weekEndM = moment(thisSunday).endOf('day')
        const weekTx = txList.filter(t => {
          const at = moment(t.get('transact_at'))
          return at.isSameOrAfter(weekStartM, 'day') && at.isSameOrBefore(weekEndM, 'day')
        })
        const total = weekTx.reduce((sum, t) => sum + (parseFloat(t.get('debit')) || 0), 0)
        setLeadSpend(total)
      } catch (err) {
        console.error('Error loading marketing data:', err)
        setLeadSpend(0)
      }
    }
    load()
  }, [])

  const onKeyPress = evt => {
    if (!/\d/.test(`${evt.key}`)) evt.preventDefault()
  }

  const handleSave = async () => {
    const val = parseInt(invested, 10)
    if (isNaN(val) || val < 0) {
      toast.error('Enter a valid number.')
      return
    }
    setSaving(true)
    try {
      const thisMonday = getThisMonday()
      if (entryId) {
        await UserPerformanceEntryService.update(entryId, { invested: val })
      } else {
        await UserPerformanceEntryService.store({
          user_id: UserProfileService.getUserId(),
          entry_date: thisMonday,
          invested: val,
        })
      }
      toast.success('Saved.')
    } catch (err) {
      console.error('Error saving commitment:', err)
      toast.error('Failed to save.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <MDBContainer id='WeeklyMarketingCommitmentForm' fluid>
      <p>Marketing</p>
      <MDBRow className='metric-intake'>
        <MDBCol size='10'>
          <label className='text-indigo' htmlFor='investedInput'>
            Week {weekStart ? moment(weekStart, 'YYYY-MM-DD').format('w') : ''} commitment
          </label>
          <br />
          <span>How much you&apos;re committing to marketing (editable)</span>
        </MDBCol>
        <MDBCol size='2'>
          <MDBInput
            type='text'
            className='form-control'
            id='investedInput'
            name='invested'
            value={invested}
            onKeyPress={onKeyPress}
            onChange={e => {
              const v = e.target.value
              if (v === '' || !isNaN(v)) setInvested(v === '' ? 0 : parseInt(v, 10) || 0)
            }}
          />
        </MDBCol>
      </MDBRow>
      <MDBRow className='metric-intake'>
        <MDBCol size='10'>
          <label className='text-indigo' htmlFor='leadSpendInput'>
            Lead spend this week
          </label>
          <br />
          <span>From ledger (Lead Credit / vendor transfers)</span>
        </MDBCol>
        <MDBCol size='2'>
          <MDBInput
            type='text'
            className='form-control'
            id='leadSpendInput'
            value={leadSpend.toFixed(2)}
            disabled
            readOnly
          />
        </MDBCol>
      </MDBRow>
      <MDBRow className='justify-content-end mt-2'>
        <MDBCol size='auto'>
          <MDBBtn type='button' color='primary' onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save'}
          </MDBBtn>
        </MDBCol>
      </MDBRow>
    </MDBContainer>
  )
}

export default observer(WeeklyMarketingCommitmentForm)
